import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, MapPin, Clock, Banknote, Phone, Mail, MessageCircle, FileText,
  Users, Lock, ShieldCheck, Heart, Sparkles, ImageIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import QuoteBuilder from "@/components/QuoteBuilder";

const URG_LABEL = { urgent: "Urgent", "1-semaine": "Sous 1 semaine", "1-mois": "Sous 1 mois", flexible: "Flexible" };
const URG_COLOR = { urgent: "bg-destructive/10 text-destructive", "1-semaine": "bg-brand/10 text-brand", "1-mois": "bg-pro/10 text-pro", flexible: "bg-slate-200 text-slate-600" };

export default function LeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [liked, setLiked] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${id}`);
      setLead(data);
      setLiked(data.is_liked);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Demande introuvable");
      nav(-1);
    } finally {
      setLoading(false);
    }
  };

  const unlock = async () => {
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/unlock`);
      track("lead_unlocked", { lead_id: lead.id, price: lead.unlock_price });
      toast.success("Demande débloquée !");
      await refresh?.();
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleLike = async () => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/like`);
      setLiked(data.liked);
      setLead((L) => ({ ...L, likes: (L.likes || 0) + (data.liked ? 1 : -1) }));
    } catch {}
  };

  if (loading || !lead) {
    return (
      <div className="app-shell grain-bg flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-brand/20 border-t-brand animate-spin" />
      </div>
    );
  }

  const isPro = user?.role === "pro";
  const canRespond = lead.is_unlocked && lead.owner_id;
  const slotsLeft = Math.max(0, (lead.unlock_slots || 3) - (lead.unlocked_count || 0));

  return (
    <div className="app-shell grain-bg pb-32">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur-lg border-b border-border px-5 py-4 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="tap-scale" data-testid="lead-detail-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-heading font-bold text-base flex-1 truncate">{lead.title}</h1>
        <button onClick={toggleLike} className="tap-scale" data-testid="lead-detail-like">
          <Heart className={`w-5 h-5 ${liked ? "fill-brand text-brand" : "text-slate-400"}`} />
        </button>
      </header>

      <div className="px-5 py-5 space-y-5">
        {/* Badges */}
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${URG_COLOR[lead.urgency]}`}>
            {URG_LABEL[lead.urgency]}
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-bone text-slate2">
            {lead.trade_name}
          </span>
          {lead.is_unlocked && !lead.is_owner && isPro && (
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-forest/10 text-forest flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Débloqué
            </span>
          )}
        </div>

        {/* Meta */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-2xl bg-white border border-border p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate2 font-semibold flex items-center gap-1"><MapPin className="w-3 h-3" /> Lieu</div>
            <div className="text-sm font-semibold text-ink mt-1">{lead.area ? `${lead.area}, ` : ""}{lead.city}</div>
            {lead.distance_km != null && <div className="text-xs text-slate2">à {lead.distance_km} km de vous</div>}
          </div>
          <div className="rounded-2xl bg-white border border-border p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate2 font-semibold flex items-center gap-1"><Banknote className="w-3 h-3" /> Budget</div>
            <div className="text-sm font-semibold text-ink mt-1">{lead.budget_min}–{lead.budget_max}€</div>
          </div>
        </div>

        {/* Concurrence — signal fort */}
        <div className="rounded-2xl bg-pro text-white p-4" data-testid="lead-competition">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-brand" />
            <div className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">Concurrence</div>
          </div>
          <div className="mt-1 font-heading font-bold text-lg">
            {lead.unlocked_count} / {lead.unlock_slots} artisan{lead.unlocked_count > 1 ? "s" : ""} ont accès
          </div>
          <div className="text-xs text-white/80 mt-1">
            {slotsLeft > 0
              ? `Il reste ${slotsLeft} place${slotsLeft > 1 ? "s" : ""}. Répondez vite pour être choisi.`
              : "Toutes les places sont prises."}
          </div>
        </div>

        {/* Description */}
        <div className="rounded-2xl bg-white border border-border p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate2 font-semibold mb-1">Description</div>
          <p className="text-sm text-ink leading-relaxed whitespace-pre-line">{lead.description}</p>
        </div>

        {/* Photos si présentes */}
        {lead.photos?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate2 font-semibold mb-2 flex items-center gap-1">
              <ImageIcon className="w-3 h-3" /> Photos jointes
            </div>
            <div className="grid grid-cols-3 gap-2">
              {lead.photos.map((p, i) => (
                <img key={i} src={p} alt="" className="rounded-xl aspect-square object-cover" loading="lazy" />
              ))}
            </div>
          </div>
        )}

        {/* Coordonnées client — visible seulement si unlocked */}
        {lead.is_unlocked ? (
          <div className="rounded-2xl bg-forest/5 border border-forest/20 p-4 space-y-2.5" data-testid="lead-client-contact">
            <div className="text-[10px] uppercase tracking-widest text-forest font-bold">Coordonnées du client</div>
            <div className="text-sm font-semibold text-ink">{lead.owner_name}</div>
            {lead.owner_phone && (
              <a href={`tel:${lead.owner_phone}`} className="flex items-center gap-2 text-sm text-ink font-medium" data-testid="lead-client-phone">
                <Phone className="w-4 h-4 text-forest" /> {lead.owner_phone}
              </a>
            )}
            {lead.owner_email && (
              <a href={`mailto:${lead.owner_email}`} className="flex items-center gap-2 text-sm text-ink font-medium break-all" data-testid="lead-client-email">
                <Mail className="w-4 h-4 text-forest" /> {lead.owner_email}
              </a>
            )}
          </div>
        ) : (
          <div className="rounded-2xl bg-white border-2 border-dashed border-border p-4 flex items-center gap-3" data-testid="lead-locked-hint">
            <Lock className="w-5 h-5 text-slate-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-sm text-ink">Coordonnées masquées</div>
              <div className="text-xs text-slate2">Débloquez pour voir le nom, le téléphone et l&apos;email du client.</div>
            </div>
          </div>
        )}

        {/* Actions Pro */}
        {isPro && !lead.is_owner && (
          <div className="grid grid-cols-1 gap-2 pt-1">
            {canRespond ? (
              <>
                <Button
                  onClick={() => { track("lead_message_started", { lead_id: lead.id }); nav(`/thread/${lead.id}/${lead.owner_id}`); }}
                  className="w-full rounded-full bg-pro hover:bg-pro-hover text-white py-6 text-base font-semibold"
                  data-testid="lead-respond-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Répondre à la demande
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { track("quote_started", { lead_id: lead.id }); setQuoteOpen(true); }}
                  className="w-full rounded-full border-pro text-pro py-6 text-base font-semibold hover:bg-pro/5"
                  data-testid="lead-quote-btn"
                >
                  <FileText className="w-4 h-4 mr-2" /> Faire un devis
                </Button>
              </>
            ) : (
              <Button
                onClick={unlock}
                disabled={busy || slotsLeft === 0}
                className="w-full rounded-full bg-pro hover:bg-pro-hover text-white py-6 text-base font-semibold disabled:opacity-50"
                data-testid="lead-unlock-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                {slotsLeft === 0
                  ? "Toutes les places sont prises"
                  : busy ? "Déblocage…" : `Débloquer (${lead.unlock_price} €)`}
              </Button>
            )}
            {!user?.subscription_active && slotsLeft > 0 && (
              <button
                onClick={() => nav("/pro/choose-plan")}
                className="w-full rounded-2xl border border-brand/40 bg-brand-light p-3 text-left tap-scale"
                data-testid="lead-upsell-premium"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-brand" />
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-wider text-brand font-semibold">Premium 9,99 €/mois</div>
                    <div className="text-sm font-heading font-bold text-ink">Déblocage illimité + priorité + IA Pro</div>
                  </div>
                </div>
              </button>
            )}
          </div>
        )}

        {lead.is_owner && (
          <div className="rounded-2xl bg-brand-light border border-brand/30 p-3 text-xs text-ink flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-brand" /> C&apos;est votre demande. {lead.unlocked_count} artisan(s) l&apos;ont débloquée.
          </div>
        )}

        {/* Meta bas de page */}
        <div className="flex items-center gap-3 text-xs text-slate2 pt-2">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(lead.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {lead.likes || 0}</span>
        </div>
      </div>

      {/* Quote builder */}
      {lead.owner_id && (
        <QuoteBuilder
          open={quoteOpen}
          onOpenChange={setQuoteOpen}
          leadId={lead.id}
          leadTitle={lead.title}
          partId={lead.owner_id}
          onCreated={() => { setQuoteOpen(false); toast.success("Devis envoyé !"); }}
        />
      )}
    </div>
  );
}
